import { computed, Injectable, signal, Signal } from '@angular/core';
import {
  PdfDocument,
  PdfFitMode,
  PdfRotation,
  PdfSearchSummary,
  PdfState,
  PdfStatus,
  PdfTheme,
  PdfViewerError,
} from '../models/pdf-viewer.model';

/**
 * Per-instance reactive store for one viewer. Provided in the component's `providers` array so
 * every `<app-pdf-viewer>` gets an isolated state object; the component and the feature services
 * read/write through this single source of truth, which keeps the component template thin and the
 * services free of DOM concerns.
 */
@Injectable()
export class PdfStateService {
  private readonly _status = signal<PdfStatus>('idle');
  private readonly _document = signal<PdfDocument | null>(null);
  private readonly _currentPage = signal(1);
  private readonly _zoom = signal(100);
  private readonly _fitMode = signal<PdfFitMode>('auto');
  private readonly _rotation = signal<PdfRotation>(0);
  private readonly _theme = signal<PdfTheme>('light');
  private readonly _sidebarOpen = signal(true);
  private readonly _fullscreen = signal(false);
  private readonly _searchOpen = signal(false);
  private readonly _searchSummary = signal<PdfSearchSummary | null>(null);
  private readonly _activeMatchIndex = signal(-1);
  private readonly _error = signal<PdfViewerError | null>(null);
  private readonly _loadProgress = signal(0);

  readonly status = this._status.asReadonly();
  readonly document = this._document.asReadonly();
  readonly currentPage = this._currentPage.asReadonly();
  readonly zoom = this._zoom.asReadonly();
  readonly fitMode = this._fitMode.asReadonly();
  readonly rotation = this._rotation.asReadonly();
  readonly theme = this._theme.asReadonly();
  readonly sidebarOpen = this._sidebarOpen.asReadonly();
  readonly fullscreen = this._fullscreen.asReadonly();
  readonly searchOpen = this._searchOpen.asReadonly();
  readonly searchSummary = this._searchSummary.asReadonly();
  readonly activeMatchIndex = this._activeMatchIndex.asReadonly();
  readonly error = this._error.asReadonly();
  readonly loadProgress = this._loadProgress.asReadonly();

  readonly pageCount = computed(() => this._document()?.pageCount ?? 0);
  readonly isLoading = computed(() => this._status() === 'loading');
  readonly isLoaded = computed(() => this._status() === 'loaded');
  readonly hasError = computed(() => this._status() === 'error');
  readonly canGoPrevious = computed(() => this._currentPage() > 1);
  readonly canGoNext = computed(() => this._currentPage() < this.pageCount());
  readonly totalMatches = computed(() => this._searchSummary()?.totalMatches ?? 0);

  /** Immutable snapshot used by toolbar-button predicates and tests. */
  readonly snapshot: Signal<PdfState> = computed(() => ({
    status: this._status(),
    document: this._document(),
    currentPage: this._currentPage(),
    pageCount: this.pageCount(),
    zoom: this._zoom(),
    fitMode: this._fitMode(),
    rotation: this._rotation(),
    theme: this._theme(),
    sidebarOpen: this._sidebarOpen(),
    fullscreen: this._fullscreen(),
    searchOpen: this._searchOpen(),
    searchSummary: this._searchSummary(),
    activeMatchIndex: this._activeMatchIndex(),
    error: this._error(),
    loadProgress: this._loadProgress(),
  }));

  setStatus(status: PdfStatus): void {
    this._status.set(status);
  }

  beginLoading(): void {
    this._status.set('loading');
    this._error.set(null);
    this._loadProgress.set(0);
    this._searchSummary.set(null);
    this._activeMatchIndex.set(-1);
  }

  setProgress(fraction: number): void {
    this._loadProgress.set(Math.min(1, Math.max(0, fraction)));
  }

  setDocument(document: PdfDocument): void {
    this._document.set(document);
    this._currentPage.set(1);
    this._status.set('loaded');
    this._loadProgress.set(1);
  }

  setError(error: PdfViewerError): void {
    this._error.set(error);
    this._status.set('error');
  }

  setCurrentPage(page: number): void {
    const count = this.pageCount();
    const clamped = count > 0 ? Math.min(count, Math.max(1, Math.round(page))) : 1;
    if (clamped !== this._currentPage()) {
      this._currentPage.set(clamped);
    }
  }

  setZoom(zoom: number): void {
    this._zoom.set(Math.round(zoom));
  }

  setFitMode(mode: PdfFitMode): void {
    this._fitMode.set(mode);
  }

  setRotation(rotation: PdfRotation): void {
    this._rotation.set(rotation);
  }

  rotate(direction: 'left' | 'right'): PdfRotation {
    const delta = direction === 'right' ? 90 : -90;
    const next = (((this._rotation() + delta) % 360) + 360) % 360;
    this._rotation.set(next as PdfRotation);
    return next as PdfRotation;
  }

  setTheme(theme: PdfTheme): void {
    this._theme.set(theme);
  }

  toggleTheme(): PdfTheme {
    const next: PdfTheme = this._theme() === 'dark' ? 'light' : 'dark';
    this._theme.set(next);
    return next;
  }

  setSidebarOpen(open: boolean): void {
    this._sidebarOpen.set(open);
  }

  toggleSidebar(): void {
    this._sidebarOpen.update((open) => !open);
  }

  setFullscreen(active: boolean): void {
    this._fullscreen.set(active);
  }

  setSearchOpen(open: boolean): void {
    this._searchOpen.set(open);
  }

  setSearchSummary(summary: PdfSearchSummary | null): void {
    this._searchSummary.set(summary);
    this._activeMatchIndex.set(summary && summary.totalMatches > 0 ? 0 : -1);
  }

  setActiveMatchIndex(index: number): void {
    this._activeMatchIndex.set(index);
  }

  /** Reset everything except user chrome preferences (theme, sidebar) before a new load. */
  reset(): void {
    this._status.set('idle');
    this._document.set(null);
    this._currentPage.set(1);
    this._rotation.set(0);
    this._error.set(null);
    this._loadProgress.set(0);
    this._searchOpen.set(false);
    this._searchSummary.set(null);
    this._activeMatchIndex.set(-1);
  }
}
